import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import {
  GoAFormItem,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';

interface RoadCharacteristicsComponentProps extends BaseWizardProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

const roadAligmentAMapping = [
  {
    label: 'Flat road surface',
    eCollisionLabel: 'Level',
  },
  {
    label: 'Uphill or downhill slope of road',
    helperText: 'for example the road has a slope either upward or downward',
    eCollisionLabel: 'Grade',
  },
  {
    label: 'Top of hill',
    eCollisionLabel: 'Hillcrest',
  },
  {
    label: 'Bottom of hill',
    eCollisionLabel: 'Sag (Bottom of Hill)',
  },
  {
    label: 'Unknown',
    eCollisionLabel: 'Unknown',
  },
];
const roadAligmentBMapping = [
  {
    label: 'Straight road',
    eCollisionLabel: 'Straight',
  },
  {
    label: 'Curved road',
    eCollisionLabel: 'Curve',
  },
  {
    label: 'Unknown',
    eCollisionLabel: 'Unknown',
  },
];
export const RoadCharacteristicsComponent = ({
  navigationConfiguration,
}: RoadCharacteristicsComponentProps) => {
  const dispatch = useAppDispatch();

  const defaultSelection = '0';

  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const headerErrorText = 'Make a selection for both sets of questions below';
  const roadAlignmentErrorText =
    'Choose the type of road surface you were traveling on';
  const roadStraightnessErrorText = 'Make a selection for the above question';

  const [roadAlignmentFormError, setRoadAlignmentFormError] = useState('');
  const [roadStraightnessFormErrer, setRoadStraightnessFormError] =
    useState('');

  const [roadAlighnmentSelection, setRoadAlignmentSelection] = useState(
    stateCollision.collision.roadAlignmentA
      ? stateCollision.collision.roadAlignmentA.toString()
      : defaultSelection,
  );
  const [roadStraighnessSelection, setRoadStraighnessSelection] = useState(
    stateCollision.collision.roadAlignmentB
      ? stateCollision.collision.roadAlignmentB.toString()
      : defaultSelection,
  );

  const [nextPage, setNextPage] = useState('');

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const validate = (): boolean => {
    dispatch(removeAllValidationErrorNotifications());
    let valid = true;
    if (
      !roadAlighnmentSelection ||
      roadAlighnmentSelection === defaultSelection
    ) {
      setRoadAlignmentFormError(roadAlignmentErrorText);
      valid = false;
    }

    if (
      !roadStraighnessSelection ||
      roadStraighnessSelection === defaultSelection
    ) {
      setRoadStraightnessFormError(roadStraightnessErrorText);
      valid = false;
    }

    if (!valid) {
      dispatch(
        pushNotification({
          body: headerErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
    }

    return valid;
  };
  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.roadAlignmentA = parseInt(roadAlighnmentSelection);
    updatedCollision.roadAlignmentB = parseInt(roadStraighnessSelection);

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <h2>Road characteristics</h2>
      <GoASpacer vSpacing={'m'} />
      Select the road details below or select 'Unknown' if unsure.
      <GoASpacer vSpacing={'l'} />
      <div className="large-body">
        What type of road surface were you traveling on?
      </div>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem error={roadAlignmentFormError}>
        <GoARadioGroup
          name="algnmentGroup"
          error={roadAlignmentFormError.length > 0}
          value={roadAlighnmentSelection}
          onChange={(_, value) => {
            setRoadAlignmentFormError('');
            dispatch(removeAllValidationErrorNotifications());
            setRoadAlignmentSelection(value);
          }}
        >
          {roadAligmentAMapping.map((alignment) => {
            const id = stateConfiguration.configuration.roadAlignmentA
              .find(
                (x) =>
                  x.value.toLowerCase() ===
                  alignment.eCollisionLabel.toLowerCase(),
              )
              ?.id.toString();

            const control = (
              <GoARadioItem
                value={id}
                label={alignment.label}
                key={id}
                description={
                  <span style={{ font: 'var(--goa-typography-body-xs)' }}>
                    {alignment.helperText ?? ''}
                  </span>
                }
              />
            );
            return control;
          })}
        </GoARadioGroup>
      </GoAFormItem>
      <GoASpacer vSpacing={'xl'} />
      <div className="large-body">Was the road straight or curved? </div>
      <GoASpacer vSpacing={'xs'} />
      <GoAFormItem error={roadStraightnessFormErrer}>
        <GoARadioGroup
          name="straightnessGroup"
          error={roadStraightnessFormErrer.length > 0}
          value={roadStraighnessSelection}
          onChange={(_, value) => {
            setRoadStraightnessFormError('');
            dispatch(removeAllValidationErrorNotifications());
            setRoadStraighnessSelection(value);
          }}
        >
          {roadAligmentBMapping.map((alignment) => {
            const id = stateConfiguration.configuration.roadAlignmentB
              .find(
                (x) =>
                  x.value.toLowerCase() ===
                  alignment.eCollisionLabel.toLowerCase(),
              )
              ?.id.toString();

            const control = (
              <GoARadioItem value={id} label={alignment.label} key={id} />
            );
            return control;
          })}
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        goToReview={navigationConfiguration.goToReview}
        validate={validate}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
